import { defineMutator } from '@rocicorp/zero';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { LicenceLimits, LicenceType, OrganisationStatus, OrganisationType } from '../database/schema.js';
import { zql } from '../zero-schema.gen.js';
async function nextLocalOrganisationCode(tx, prefix = 'ORG', base = 0) {
    const prefixDash = `${prefix}-`;
    const existing = await tx.run(zql.organisation.where('internalId', 'ILIKE', `${prefixDash}%`));
    const maxNum = existing
        .map(r => Number.parseInt(String(r.internalId).slice(prefixDash.length), 10))
        .filter((n) => Number.isFinite(n))
        .reduce((acc, n) => Math.max(acc, n), base) || base;
    return `${prefixDash}${maxNum + 1}`;
}
async function findById(tx, id) {
    return await tx.run(zql.organisation.where('id', '=', id).one());
}
export const AddOrganisationLicenceSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(LicenceType.enum.limited),
        limits: LicenceLimits.optional(),
        start: z.iso.date().nonoptional(),
        exp: z.iso.date().nonoptional(),
    }),
    z.object({
        type: z.literal(LicenceType.enum.lifetime),
        limits: LicenceLimits.optional(),
    }),
]);
export const AddOrganisationSchema = z.object({
    name: z.string().trim().nonempty(),
    country: z.string().trim().nonempty(),
    type: OrganisationType,
    status: OrganisationStatus.default(OrganisationStatus.enum.active),
    isDemo: z.boolean().default(false),
    licence: AddOrganisationLicenceSchema,
    street: z.string().optional(),
    admin: z.object({
        firstName: z.string().trim().nonempty(),
        lastName: z.string().trim().nonempty(),
        email: z.email().trim().nonempty(),
    }),
});
export const UpdateOrganisationSchema = AddOrganisationSchema.partial().extend({
    id: z.uuidv7(),
});
export const organisations = {
    add: defineMutator(AddOrganisationSchema, async ({ args: organisation, ctx, tx }) => {
        if (!ctx?.user)
            throw new Error('Not authenticated');
        const internalId = await nextLocalOrganisationCode(tx, 'ORG', 0);
        const now = Date.now();
        await tx.mutate.organisation.insert({
            id: uuidv7(),
            internalId,
            name: organisation.name,
            country: organisation.country,
            type: organisation.type,
            status: organisation.status,
            isDemo: organisation.isDemo,
            street: organisation.street,
            createdAt: now,
            createdBy: ctx.user.id,
        });
    }),
    update: defineMutator(UpdateOrganisationSchema, async ({ args: organisation, ctx, tx }) => {
        if (!ctx?.user)
            throw new Error('Not authenticated');
        const prev = await findById(tx, organisation.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        await tx.mutate.organisation.update({
            id: organisation.id,
            name: organisation.name,
            country: organisation.country,
            type: organisation.type,
            status: organisation.status,
            street: organisation.street,
            updatedAt: now,
            updatedBy: ctx.user.id,
        });
    }),
    delete: defineMutator(z.object({ id: z.uuidv7() }), async ({ args, ctx, tx }) => {
        if (!ctx?.user)
            throw new Error('Not authenticated');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        await tx.mutate.organisation.update({
            id: args.id,
            deletedAt: Date.now(),
            deletedBy: ctx.user.id,
        });
    }),
};
//# sourceMappingURL=organisation.mutators.js.map